import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RecentlyViewedItem } from './recently-viewed-item.interface';
import { CACHE_KEYS } from '@shared/constants';

@Injectable({
    providedIn: 'root',
})
export class RecentlyViewedService {
    private SEARCHES_KEY = CACHE_KEYS.RECENT_SEARCHES;
    private VIEWS_KEY = CACHE_KEYS.RECENT_VIEWS;
    private LAST_VALID_SEARCH_KEY = CACHE_KEYS.LAST_VALID_SEARCH;
    private maxItems = 10;

    // BehaviorSubject to track changes to recent searches
    private recentSearchesSubject = new BehaviorSubject<string[]>([]);

    // Observable that components can subscribe to
    public recentSearchesChanged$: Observable<string[]> = this.recentSearchesSubject.asObservable();

    // BehaviorSubject to track changes to recent views
    private recentViewsSubject = new BehaviorSubject<RecentlyViewedItem[]>([]);

    // Observable that components can subscribe to
    public recentViewsChanged$: Observable<RecentlyViewedItem[]> = this.recentViewsSubject.asObservable();

    constructor() {
        // Initialize the BehaviorSubjects with current data from localStorage
        this.recentSearchesSubject.next(this.getRecentSearchesFromStorage());
        this.recentViewsSubject.next(this.getRecentViewsFromStorage());
    }

    /** Get the last searches */
    get recentSearches(): string[] {
        return this.getRecentSearchesFromStorage();
    }

    /** Get recent searches from localStorage */
    private getRecentSearchesFromStorage(): string[] {
        return JSON.parse(localStorage.getItem(this.SEARCHES_KEY) || '[]');
    }

    /**
     * Add Search term to local storage
     * @param term - search term
     * @param hasResults - whether the search returned results
     * @returns
     */
    addSearch(term: string, hasResults: boolean = true): void {
        if (!term) return;
        // If the search has results, save it as the last valid search
        if (hasResults && term !== 'undefined') {
            localStorage.setItem(this.LAST_VALID_SEARCH_KEY, term);
        }

        const items = [term, ...this.recentSearches.filter((savedTerm: string) => savedTerm !== term)];
        const slicedItems = items.slice(0, this.maxItems);
        localStorage.setItem(this.SEARCHES_KEY, JSON.stringify(slicedItems));

        // Emit the updated list to all subscribers
        this.recentSearchesSubject.next(slicedItems);
    }

    /**
     * Delete a specific search term from local storage
     * @param term - The search term to delete
     */
    deleteSearch(term: string): void {
        if (!term) return;
        const items = this.recentSearches.filter((savedTerm: string) => savedTerm !== term);
        localStorage.setItem(this.SEARCHES_KEY, JSON.stringify(items));

        // Emit the updated list to all subscribers
        this.recentSearchesSubject.next(items);
    }

    /**
     * Get saved Recently Viewed items
     * @returns RecentlyViewedItem[]
     */
    get recentViews(): RecentlyViewedItem[] {
        return this.getRecentViewsFromStorage();
    }

    /** Get recent views from localStorage */
    private getRecentViewsFromStorage(): RecentlyViewedItem[] {
        return JSON.parse(localStorage.getItem(this.VIEWS_KEY) || '[]');
    }

    /**
     * Add a specific view to local storage
     * @param item - The item to add
     */
    addView(item: RecentlyViewedItem): void {
        if (!item.url) return;
        const items = [item, ...this.recentViews.filter((savedView: RecentlyViewedItem) => savedView.url !== item.url)];
        const slicedItems = items.slice(0, this.maxItems);
        localStorage.setItem(this.VIEWS_KEY, JSON.stringify(slicedItems));

        // Emit the updated list to all subscribers
        this.recentViewsSubject.next(slicedItems);
    }

    /**
     * Delete a specific view from local storage
     * @param item - The item to delete
     */
    deleteView(item: RecentlyViewedItem): void {
        if (!item.url) return;
        const items = this.recentViews.filter((savedView: RecentlyViewedItem) => savedView.url !== item.url);
        localStorage.setItem(this.VIEWS_KEY, JSON.stringify(items));

        // Emit the updated list to all subscribers
        this.recentViewsSubject.next(items);
    }

    /**
     * Clear all recently viewed searches
     */
    clearAllSearches(): void {
        localStorage.removeItem(this.SEARCHES_KEY);
        localStorage.removeItem(this.LAST_VALID_SEARCH_KEY);

        // Emit empty array to all subscribers
        this.recentSearchesSubject.next([]);
    }

    /**
     * Clear all recently viewed items
     */
    clearAllViews(): void {
        localStorage.removeItem(this.VIEWS_KEY);

        // Emit empty array to all subscribers
        this.recentViewsSubject.next([]);
    }

    /**
     * Get the last valid search term (one that returned results)
     * @returns string or null if no last valid search
     */
    getLastValidSearch(): string | null {
        return localStorage.getItem(this.LAST_VALID_SEARCH_KEY);
    }

    /**
     * Clear all recently viewed items and searches
     */
    clearAll(): void {
        this.clearAllSearches();
        this.clearAllViews();
    }
}
